import Page from 'flow/components/Setting/Page';
import Checkbox from 'flow/components/ui-components/Checkbox';
import Divider from 'flow/components/ui-components/Divider';
import KeySequence from 'flow/components/ui-components/KeySequence';
import useAppContext from 'flow/hooks/useAppContext';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { generatePath } from 'flow/utils/router-utils';
import { useHistory, useParams } from 'react-router-dom';
import { getKeyboardShortcuts } from 'flow/selectors/keyboard-shortcuts-selectors';
import { updateKeyboardShortcuts } from 'flow/actions/keyboard-shortcuts-actions';
import { KeyboardShortcut } from 'flow/types/KeyboardShortcut';
import { TypedMap } from 'flow/types/TypedMap';
import GenericSmallIconButton from 'flow/components/buttons/GenericSmallIconButton';
import { useModal } from 'flow/contexts/ModalProvider';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';

const SettingsKeyboardShortcut = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();
    const keyboardShortcuts = useAppSelector(getKeyboardShortcuts);
    const { openModal } = useModal();

    const handleToggleShortcut = (shortcut: TypedMap<KeyboardShortcut>) => {
        const updatedShortcut = shortcut.set(
            'enabled',
            !shortcut.get('enabled'),
        );
        dispatch(updateKeyboardShortcuts(updatedShortcut.toJS()));
    };

    const handleEditShortcut = (shortcut: TypedMap<KeyboardShortcut>) => {
        openModal('editKeyboardShortcut', { shortcut });
    };

    return (
        <Page
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.general'], {
                        extensionKey,
                    }),
                )
            }
            header={'settings.keyboard.shortcuts'}
        >
            {i18next.t('settings.keyboard.shortcuts.info')}

            <Divider />

            {keyboardShortcuts
                .groupBy((shortcut) => shortcut.get('group'))
                .valueSeq()
                .map((groupedShortcuts, index) => (
                    <div key={`group-${index}`}>
                        <div className="text-m p-2 font-bold">
                            {i18next.t(
                                `settings.keyboard.shortcuts.group.${groupedShortcuts.first().get('group')}`,
                            )}
                        </div>
                        {groupedShortcuts
                            .map((shortcut) => (
                                <div
                                    key={shortcut.get(
                                        'keyboardShortcutEntityId',
                                    )}
                                    className="flex items-center gap-1"
                                >
                                    <div className="flex h-13 w-full items-center justify-between rounded p-2 hover:bg-gray-100">
                                        <Checkbox
                                            key={shortcut.get(
                                                'keyboardShortcutEntityId',
                                            )}
                                            label={i18next.t(
                                                `settings.keyboard.shortcuts.label.${shortcut.get('label')}`,
                                            )}
                                            checked={shortcut.get('enabled')}
                                            onChange={() =>
                                                handleToggleShortcut(shortcut)
                                            }
                                            className="shrink-0"
                                        />
                                        <KeySequence
                                            shortcut={shortcut.get('value')}
                                        />
                                    </div>
                                    <GenericSmallIconButton
                                        onClick={() =>
                                            handleEditShortcut(shortcut)
                                        }
                                        iconKey={'edit'}
                                        label={i18next.t(
                                            'Tooltip.Nav.Title.Edit',
                                        )}
                                        className="bg-transparent text-gray-500"
                                        size="small"
                                    />
                                </div>
                            ))
                            .valueSeq()
                            .toArray()}

                        <Divider />
                    </div>
                ))}
        </Page>
    );
};

export default SettingsKeyboardShortcut;
